import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SEASONS } from '@app/activo2/manage-vacations/manage-vacations.constants';
import {
  ICreateProposal,
  IEmployeeObservations,
  IMotManageVacationsCriteria,
  IMomoManageVacationsCriteria,
  IManageVacationsProposals,
  ISeasonCalendar,
  IMomoCreateProposal,
  IMomoManageVacationsProposals
} from '@app/activo2/manage-vacations/manage-vacations.interface';
import {
  SeasonCalendar,
  MotSeasonProposal,
  MotVacationsCriteria,
  MomoVacationsCriteria,
  VacationsProposal,
  MomoVacationsProposal
} from '@app/activo2/manage-vacations/manage-vacations.model';
import { Logger } from '@app/services';
import { ErrorCodes, ErrorMessages, ErrorMsg, ErrorTypes } from '@app/services/error/error.model';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ApiMotUrls } from '@mot/services/api/api.service';

@Injectable({
  providedIn: 'root'
})
export class ManageVacationsService {
  private logger = new Logger('MotService');

  private onError = new Subject<ErrorTypes>();
  errorMsg$ = new Subject<ErrorMsg>();
  onError$ = this.onError.asObservable();

  currentEditingProposal = new BehaviorSubject<MotSeasonProposal>(new MotSeasonProposal([]));
  editedProposal = false;

  constructor(private apiUrls: ApiMotUrls, private http: HttpClient) {}

  getProposals(isMomoUser: boolean): Observable<MSafeAny> {
    if (isMomoUser) {
      return this.http.get<IMomoManageVacationsProposals>(this.apiUrls.momo_manage_vacations.proposals).pipe(
        map((proposal) => {
          if (proposal) {
            return new MomoVacationsProposal(proposal);
          }
        }),
        catchError(this.handleError)
      );
    }

    return this.http.get<IManageVacationsProposals>(this.apiUrls.mot_manage_vacations.proposals).pipe(
      map((proposal) => {
        if (proposal) {
          return new VacationsProposal(proposal);
        }
      }),
      catchError(this.handleError)
    );
  }

  getVacationsCriteria(isMomoUser: boolean): Observable<MSafeAny> {
    if (isMomoUser) {
      return this.http.get<IMomoManageVacationsCriteria>(this.apiUrls.momo_manage_vacations.criteria).pipe(
        map((criteria) => new MomoVacationsCriteria(criteria)),
        catchError(this.handleError)
      );
    }

    return this.http.get<IMotManageVacationsCriteria>(this.apiUrls.mot_manage_vacations.criteria).pipe(
      map((criteria) => new MotVacationsCriteria(criteria)),
      catchError(this.handleError)
    );
  }

  getSeasonCalendar(season: SEASONS, isMomoUser: boolean): Observable<MSafeAny> {
    let params = new HttpParams();
    params = params.set('season', season);

    if (isMomoUser) {
      return this.http.get<ISeasonCalendar>(this.apiUrls.momo_manage_vacations.calendar, { params }).pipe(
        map((calendar) => new SeasonCalendar(calendar)),
        catchError(this.handleError)
      );
    }

    return this.http.get<ISeasonCalendar>(this.apiUrls.mot_manage_vacations.calendar, { params }).pipe(
      map((calendar) => new SeasonCalendar(calendar)),
      catchError(this.handleError)
    );
  }

  createProposal(proposalData: ICreateProposal | IMomoCreateProposal, isMomoUser: boolean): Observable<MSafeAny> {
    if (isMomoUser) {
      return this.http
        .post<IMomoManageVacationsProposals>(
          this.apiUrls.momo_manage_vacations.proposals,
          proposalData as IMomoCreateProposal
        )
        .pipe(map((proposal) => new MomoVacationsProposal(proposal)));
    }

    return this.http
      .post<IManageVacationsProposals>(this.apiUrls.mot_manage_vacations.proposals, proposalData as ICreateProposal)
      .pipe(map((proposal) => new VacationsProposal(proposal)));
  }

  updateObservations(proposalData: IEmployeeObservations, isMomoUser: boolean): Observable<MSafeAny> {
    if (isMomoUser) {
      return this.http
        .put<IEmployeeObservations>(this.apiUrls.momo_manage_vacations.observations, proposalData)
        .pipe(catchError((err) => (err.status === ErrorCodes.BAD_GATEWAY ? err : this.handleError)));
    }

    return this.http
      .put<IEmployeeObservations>(this.apiUrls.mot_manage_vacations.observations, proposalData)
      .pipe(catchError((err) => (err.status === ErrorCodes.BAD_GATEWAY ? err : this.handleError)));
  }

  private handleError = (error: HttpErrorResponse): Observable<MSafeAny[]> => {
    let errorType = ErrorTypes.OTHER;

    if (error.status === ErrorCodes.SECTION_MAINTENANCE && error.error.code === ErrorMessages.PERM_DEACTIVATED) {
      errorType = ErrorTypes.SECTION_MAINTENANCE;
    }

    if (error.status === ErrorCodes.BAD_GATEWAY && error.error.code === ErrorMessages.EMPTY_SLOTS) {
      errorType = ErrorTypes.EMPTY_SLOTS;
    }

    this.onError.next(errorType);

    if (errorType === ErrorTypes.EMPTY_SLOTS) {
      this.errorMsg$.next({ code: error.error.code, message: error.error.message });
    }

    this.logger.error(error);

    return of();
  };
}
